import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { AxiosError } from "axios";
import { IBaseResponse } from "@/apps/credit-line/models/IBaseResponse";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";

declare const Android: AndroidInterface;
type callbackErrorHandler = (error: Error) => void;
type callbackNetworkErrorHandler = (error: AxiosError) => void;

export const ERROR_CODES = {
  START_SESSION_API_ERROR: 1001,
  START_SESSION_NETWORK_ERROR: 1002,
  LOG_TRANSITION_NETWORK_ERROR: 1003,
  LOG_TRANSITION_API_ERROR: 1004,
  GET_INCODE_URL_API_ERROR: 1005,
  GET_INCODE_URL_NETWORK_ERROR: 1006,
  GET_INCODE_STATUS_API_ERROR: 1007,
  GET_INCODE_STATUS_NETWORK_ERROR: 1008,
  CLOSE_SESSION_API_ERROR: 1009,
  CLOSE_SESSION_NETWORK_ERROR: 1010,
  VALIDATE_CURP_API_ERROR: 1011,
  VALIDATE_CURP_NETWORK_ERROR: 1012,
  CREDIT_PULL_API_ERROR: 1013,
  CREDIT_PULL_NETWORK_ERROR: 1014,
  PERMISSIONS_NETWORK_ERROR: 1015,
  CURP_CHALLENGE_API_ERROR: 2001,
  CURP_CHALLENGE_NETWORK_ERROR: 2002,
};

export class ErrorHandler {
  static handleError(
    error: Error,
    onErrorCallback?: callbackErrorHandler
  ): void {
    console.log(error);
    onErrorCallback && onErrorCallback(error);
  }

  static logNetworkError(error: AxiosError, errorCode: number): void {
    console.error(error);
    const errorName = Object.keys(ERROR_CODES).find(
      (key) => ERROR_CODES[key] === errorCode
    );
    const responseData = error?.response?.data;
    let errorMsg: string;
    let context = {};
    if (responseData) {
      errorMsg = `CoreApp Onboarding Error: ${errorName}[${errorCode}] - ${responseData.message}`;
      context = responseData.data;
    } else {
      errorMsg = `CoreApp Onboarding Error: ${errorName}[${errorCode}] - ${error.message}`;
      context = error;
    }
    const deviceTag =
      typeof Android !== "undefined" ? Android.getDeviceTag() : null;
    DatadogLogger.Log(
      errorMsg,
      {
        ...context,
        deviceTag,
        errorCode,
        errorName,
      },
      LogType.ERROR
    );
  }

  static logAPIError(response: IBaseResponse, errorCode: number): void {
    console.error(response.error);
    const errorName = Object.keys(ERROR_CODES).find(
      (key) => ERROR_CODES[key] === errorCode
    );
    const errorMsg = `CoreApp Onboarding Error: [${errorCode}] - ${response?.error}`;
    const deviceTag =
      typeof Android !== "undefined" ? Android.getDeviceTag() : null;
    DatadogLogger.Log(
      errorMsg,
      {
        ...response,
        deviceTag,
        errorCode,
        errorName,
      },
      LogType.ERROR
    );
  }

  static handleNetworkErrorAndLog(
    error: AxiosError,
    onErrorCallback?: callbackNetworkErrorHandler,
    apiName?: string
  ): void {
    const responseData = error?.response?.data;
    let errorMsg: string;
    let data: unknown;

    if (responseData) {
      errorMsg = responseData?.errorCode
        ? `Credit Line Error: ${responseData?.errorCode} | ${JSON.stringify(
            error.message
          )}`
        : `Credit Line Error: ${JSON.stringify(error.message)}`;
      data = responseData.data;
    } else {
      errorMsg = `Credit Line Error: ${JSON.stringify(error.message)}`;
      data = error;
    }

    // Get device tag safely
    const deviceTag = AuthenticationHelper.getDeviceTag();

    // Create a properly typed context object
    const context: Record<string, unknown> = {
      data,
      deviceTag,
      apiName,
      errorMsg,
      url: error.config?.url,
      method: error.config?.method,
      statusCode: error.response?.status,
      responseData,
    };

    DatadogLogger.Log(errorMsg, context, LogType.ERROR);
    onErrorCallback && onErrorCallback(error);
  }

  static logCreditlineErrorResponse(response: IBaseResponse): void {
    const errorMsg = response?.code
      ? `Credit Line Error: ${JSON.stringify(response)}`
      : `Credit Line Error: ${JSON.stringify(response)}`;

    const context =
      typeof Android !== typeof undefined
        ? { ...response, deviceTag: Android.getDeviceTag() }
        : response;

    DatadogLogger.Log(
      errorMsg,
      context as Record<string, unknown>,
      LogType.ERROR
    );
  }
}
